// Code Connect: link a design component to the code component that implements it, so Dev Mode,
// code export and agents show real usage (`<Button size="lg" label="Buy" />`) instead of
// generated markup. The mapping lives on the component (or its component set).

import type { Document } from './document.ts';
import type { NodeData, NodeId } from './types.ts';
import { componentSetOf, parseVariantName } from './variants.ts';

export type CodeConnect = NonNullable<NodeData['codeConnect']>;
export type CodeConnectProp = NonNullable<CodeConnect['props']>[number];

/** The mapping that applies to a component or instance, with the component it's on. */
export function codeConnectOf(doc: Document, id: NodeId): { mapping: CodeConnect; on: NodeData } | null {
  const n = doc.get(id);
  if (!n) return null;
  const comp = n.type === 'INSTANCE' ? doc.get(n.componentId ?? '') : n.type === 'COMPONENT' ? n : n.isComponentSet ? n : null;
  if (!comp) return null;
  if (comp.codeConnect) return { mapping: comp.codeConnect, on: comp };
  const set = comp.isComponentSet ? comp : componentSetOf(doc, comp.id);
  return set?.codeConnect ? { mapping: set.codeConnect, on: set } : null;
}

type PropValue = { kind: 'VARIANT' | 'BOOLEAN' | 'TEXT' | 'INSTANCE_SWAP'; value: string | boolean };

/** An instance's design properties and their current values (variants, then component properties). */
export function designProps(doc: Document, id: NodeId): Record<string, PropValue> {
  const n = doc.get(id);
  if (!n) return {};
  const comp = n.type === 'INSTANCE' ? doc.get(n.componentId ?? '') : n;
  if (!comp) return {};
  const out: Record<string, PropValue> = {};
  if (componentSetOf(doc, comp.id)) for (const [k, v] of Object.entries(parseVariantName(comp.name))) out[k] = { kind: 'VARIANT', value: v };
  for (const [k, def] of Object.entries(comp.componentProps ?? {})) {
    const v = n.type === 'INSTANCE' ? (n.propValues?.[k] ?? def.default) : def.default;
    out[k] = { kind: def.type, value: v };
  }
  return out;
}

const camel = (s: string) => s.trim().replace(/[^A-Za-z0-9]+(.)?/g, (_, c: string | undefined) => (c ? c.toUpperCase() : '')).replace(/^[A-Z]/, (c) => c.toLowerCase()) || 'prop';

/** Code for one connected instance (null when its component isn't connected). */
export function connectSnippet(doc: Document, id: NodeId, depth = 0): { jsx: string; imports: string[] } | null {
  const hit = codeConnectOf(doc, id);
  if (!hit || depth > 6) return null;
  const { mapping } = hit;
  const imports = mapping.import ? [mapping.import] : [];
  const attrs: string[] = [];
  let children: string | null = null;
  const listed = new Map((mapping.props ?? []).map((p) => [p.figma.toLowerCase(), p]));
  for (const [name, { kind, value }] of Object.entries(designProps(doc, id))) {
    const rule = listed.get(name.toLowerCase());
    const prop = rule?.prop ?? camel(name);
    let v: string | number | boolean = typeof value === 'string' && rule?.map && value in rule.map ? rule.map[value] : value;
    if (kind === 'VARIANT' && !(rule?.map && String(value) in rule.map)) {
      // variant values: True/False become booleans, others a code-style string
      if (/^(true|false)$/i.test(String(v))) v = /^true$/i.test(String(v));
      else v = String(v).trim().toLowerCase().replace(/\s+/g, '-');
    }
    if (kind === 'INSTANCE_SWAP') {
      const nested = typeof value === 'string' ? swapSnippet(doc, value, depth) : null;
      if (nested) {
        imports.push(...nested.imports);
        if (prop === 'children') children = nested.jsx;
        else attrs.push(`${prop}={${nested.jsx}}`);
      }
      continue;
    }
    if (prop === 'children') {
      children = escapeJsx(String(v));
      continue;
    }
    if (v === true) attrs.push(prop);
    else if (v === false) continue;
    else if (typeof v === 'number') attrs.push(`${prop}={${v}}`);
    else attrs.push(`${prop}=${JSON.stringify(v)}`);
  }
  const open = `${mapping.component}${attrs.length ? ' ' + attrs.join(' ') : ''}`;
  return { jsx: children !== null ? `<${open}>${children}</${mapping.component}>` : `<${open} />`, imports };
}

/** A swapped-in component (by id) as code: connected components only. */
function swapSnippet(doc: Document, componentId: NodeId, depth: number) {
  const comp = doc.get(componentId);
  if (!comp) return null;
  return connectSnippet(doc, comp.id, depth + 1);
}

/** Connected instances inside `ids` (outermost only), as an import block plus usages. */
export function connectCode(doc: Document, ids: NodeId[]): string | null {
  const found: { jsx: string; imports: string[] }[] = [];
  const visit = (id: NodeId) => {
    const n = doc.get(id);
    if (!n) return;
    if (n.type === 'INSTANCE') {
      const s = connectSnippet(doc, id);
      if (s) return void found.push(s);
    }
    for (const c of doc.children(id)) visit(c.id);
  };
  ids.forEach(visit);
  if (!found.length) return null;
  const imports = [...new Set(found.flatMap((f) => f.imports))];
  return [...(imports.length ? [imports.join('\n'), ''] : []), ...found.map((f) => f.jsx)].join('\n');
}

function escapeJsx(s: string) {
  return s.replace(/[{}<>]/g, (c) => `{${JSON.stringify(c)}}`);
}
